import { IButtonStyles } from '@fluentui/react';
import { concatStyleSets, FontWeights, ITheme } from '@fluentui/react/lib/Styling';
import { memoizeFunction } from '@fluentui/react/lib/Utilities';

import {
  ActionControlSemanticColors,
  baseFabricFocusSelector,
  buttonBorderWidth,
  commonHighContrastHoverFocusStyles,
  controlStylingConstants,
  getActionControlSemanticColorsWithTheme,
  getCommonDisabledIsFocusVisibleStyles,
} from '@1js/acui-common';

import { getDisabledAppToggleButtonHoverActiveStyles } from './AppBaseButton.styles';

export const buttonPadding = 9;

const actionButtonStyles = (theme: ITheme): IButtonStyles => {
  const actionControlSemanticColors: ActionControlSemanticColors = getActionControlSemanticColorsWithTheme(theme);
  return {
    root: [
      {
        backgroundColor: 'transparent',
        color: actionControlSemanticColors.textColor,
        fontWeight: 'normal',
        borderRadius: 0,
        border: `${buttonBorderWidth}px solid transparent`,
        height: controlStylingConstants.controlHeight,
        padding: `0 ${buttonPadding}px`,
      },
      {
        selectors: {
          ':hover': {
            backgroundColor: actionControlSemanticColors.hoverBackgroundColor,
            color: actionControlSemanticColors.textColor,
          },
          ':active': {
            backgroundColor: actionControlSemanticColors.pressedBackgroundColor,
          },
          [baseFabricFocusSelector]: {
            selectors: {
              ':active': {
                backgroundColor: actionControlSemanticColors.pressedBackgroundColor,
              },
            },
          },
        },
      },
    ],

    rootDisabled: {
      backgroundColor: 'transparent',
      color: actionControlSemanticColors.textDisabledColor,
      border: '1px solid transparent',
      selectors: {
        ':hover': {
          backgroundColor: 'transparent',
        },
        ':active': {
          backgroundColor: 'transparent',
        },
        [baseFabricFocusSelector]: [
          [
            getCommonDisabledIsFocusVisibleStyles(theme),
            { backgroundColor: actionControlSemanticColors.focusDisabledBackgroundColor },
          ],
        ],
      },
    },

    rootChecked: {
      backgroundColor: actionControlSemanticColors.toggledBackgroundColor,
      color: actionControlSemanticColors.textColor,
      selectors: {
        [baseFabricFocusSelector]: {
          backgroundColor: actionControlSemanticColors.toggledBackgroundColor,
          selectors: {
            ':hover': {
              backgroundColor: actionControlSemanticColors.hoverBackgroundColor,
            },
          },
        },
      },
    },

    rootExpanded: {
      backgroundColor: actionControlSemanticColors.toggledBackgroundColor,
      selectors: {
        ':hover': {
          backgroundColor: actionControlSemanticColors.hoverBackgroundColor,
        },
      },
    },

    rootCheckedHovered: {
      backgroundColor: actionControlSemanticColors.hoverBackgroundColor,
    },

    rootCheckedDisabled: {
      backgroundColor: actionControlSemanticColors.toggledBackgroundColor,
      selectors: {
        ':hover': [getDisabledAppToggleButtonHoverActiveStyles(theme), commonHighContrastHoverFocusStyles],
        ':active': [getDisabledAppToggleButtonHoverActiveStyles(theme), commonHighContrastHoverFocusStyles],
        [baseFabricFocusSelector]: [
          [
            getCommonDisabledIsFocusVisibleStyles(theme),
            { backgroundColor: actionControlSemanticColors.disabledBackgroundColor },
          ],
        ],
      },
    },

    label: {
      marginRight: 2,
      marginLeft: 10,
      fontWeight: FontWeights.regular,
    },

    menuIcon: {
      marginRight: 2,
    },
  };
};

export const getActionButtonStyles = memoizeFunction(
  (theme: ITheme, customStyles?: IButtonStyles): IButtonStyles => {
    // Task 3451776: Cleanup UI After New Redline BRS Is Validated
    const styles: IButtonStyles = actionButtonStyles(theme);
    return concatStyleSets(styles, customStyles);
  }
);
